import { ChartNoAxesGantt, GalleryVerticalEnd, NotebookPen } from "lucide-react";
import { useMemo, useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Can } from "@/components/can";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCurrentUser } from "@/hooks/use-current-user";

import type { ClaimWithRelations } from "../../api/use-claim";
import { ClaimNoteEditor } from "./claim-note-editor";
import { ClaimNoteElement } from "./claim-note-element";
import { ClaimTimelineElement } from "./claim-timeline-element";
import { buildTimelineItems, type TimelineItem } from "./timeline-utils";

interface ClaimTimelineProps {
  claim: ClaimWithRelations;
}

/** Port of timeline/ClaimTimeline.vue. */
export function ClaimTimeline({ claim }: ClaimTimelineProps) {
  const { can } = useCurrentUser();
  const [activeTab, setActiveTab] = useState("all");

  const canSeePrivateNotes = can([...UPCLAIM_ROLES, "LAWYER", "PLEADING_LAWYER"]);

  const allTimelineItems = useMemo(
    () => buildTimelineItems(claim, canSeePrivateNotes),
    [claim, canSeePrivateNotes],
  );
  const eventsOnly = allTimelineItems.filter((item) => item.type === "event");
  const notesOnly = allTimelineItems.filter((item) => item.type === "note");

  function renderItem(item: TimelineItem, index: number, items: TimelineItem[]) {
    if (item.type === "event") {
      const nextItem = items[index + 1];
      return (
        <ClaimTimelineElement
          key={`event-${item.data.id}`}
          claim={claim}
          event={item.data}
          previousEvent={nextItem?.type === "event" ? nextItem.data : undefined}
          index={index}
          timelineLength={items.length}
        />
      );
    }
    return <ClaimNoteElement key={`note-${item.data.id}`} claimId={claim.id} note={item.data} />;
  }

  return (
    <section aria-labelledby="timeline-title" className="max-w-full">
      <div className="align-middle shadow sm:rounded-lg">
        <div className="divide-y divide-gray-200">
          <div className="bg-white text-left">
            <div className="border-b border-gray-200 px-6 py-5">
              <h2 id="timeline-title" className="text-xl font-medium leading-6 text-gray-900">
                Timeline
              </h2>
              <p className="mt-1 max-w-2xl text-sm font-normal text-gray-500">
                Les derniers éléments de votre dossier.
              </p>
            </div>

            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as string)}>
              <div className="border-b border-gray-200">
                <TabsList variant="underline">
                  <TabsTrigger value="all" className="gap-2">
                    <GalleryVerticalEnd />
                    Tous ({allTimelineItems.length})
                  </TabsTrigger>
                  <TabsTrigger value="events" className="gap-2">
                    <ChartNoAxesGantt />
                    Évènements ({eventsOnly.length})
                  </TabsTrigger>
                  <TabsTrigger value="notes" className="gap-2">
                    <NotebookPen />
                    Notes internes ({notesOnly.length})
                  </TabsTrigger>
                </TabsList>
              </div>

              <Can permissions={[...UPCLAIM_ROLES, "LAWYER", "PLEADING_LAWYER"]}>
                <div className="mt-4">
                  <ClaimNoteEditor claimId={claim.id} />
                </div>
              </Can>

              <TabsContent value="all" className="mt-0">
                <div className="px-5">
                  <ul className="relative w-full border-l border-gray-200">
                    {allTimelineItems.map((item, index) => renderItem(item, index, allTimelineItems))}
                  </ul>
                </div>
              </TabsContent>

              <TabsContent value="events" className="mt-0">
                <div className="px-5">
                  <ul className="relative w-full border-l border-gray-200">
                    {eventsOnly.map((item, index) => renderItem(item, index, eventsOnly))}
                  </ul>
                </div>
              </TabsContent>

              <TabsContent value="notes" className="mt-0">
                <div className="px-5">
                  <ul className="relative w-full border-l border-gray-200">
                    {notesOnly.map((item, index) => renderItem(item, index, notesOnly))}
                  </ul>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <nav
            className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-5 sm:px-6"
            aria-label="Pagination"
          >
            <div className="hidden sm:block">
              <p className="text-sm text-gray-700">
                Affichage de <span className="font-medium">{allTimelineItems.length}</span> éléments
              </p>
            </div>
          </nav>
        </div>
      </div>
    </section>
  );
}
